import { Fragment, useEffect, useState } from "react";
import { Alert, Button, Form, Table } from "react-bootstrap";
import RulesHelp from "../components/shared/RulesHelp.jsx";
import LoggedAcceptedHeadline from "../components/reports/LoggedAcceptedHeadline.jsx";
import { getLoggedAcceptedReport } from "../services/apiClient.js";
import { todayInput } from "../utils/date.js";
import "../styles/loggedAccepted.css";

// The Jobs Logged / Accepted report — the office-favourite WebForms report
// (JobsLoggedAcceptedReport.aspx) recreated structurally: weekly rows grouped
// by month, 50/week target with red/green +/-, monthly rollups, and the three
// prior years alongside. Rules in services/loggedAcceptedReport.js.
// Styling follows the house Bootstrap table model (no striped/hover: the
// rowspan month/rollup cells would misalign both).

// Legacy week-date style: "29 Dec - 04 Jan" (no years, no noise). Safe as it
// stands: a date-only string parsed with an explicit T00:00:00 is LOCAL midnight,
// so the day and month it formats are the ones in the string.
const shortDate = (isoDate) =>
  new Date(`${isoDate}T00:00:00`).toLocaleDateString("en-NZ", { day: "2-digit", month: "short" });

// The NZ year. `new Date().getFullYear()` is the BROWSER's year, which on a
// Brisbane box is two hours behind Auckland: on 1 January the report opened on
// last year until the dev machine caught up. Same family as the toISOString ban
// (CLAUDE.md) — a date read off the wrong clock — just wearing different clothes.
const currentYear = () => Number(todayInput().slice(0, 4));

function LoggedAcceptedReportPage() {
  const [report, setReport] = useState(null);
  const [year, setYear] = useState(currentYear);
  const [error, setError] = useState("");

  // Deliberately NOT behind the reports master switch (Robert, reconfirmed
  // 2026-07-16): like the Dashboard header, this one always shows — and so it
  // always wears the "live" icon in the Reports menu.
  useEffect(() => {
    setReport(null);
    getLoggedAcceptedReport(year)
      .then(setReport)
      .catch((err) => setError(err.response?.data?.error || "Unable to load the report."));
  }, [year]);

  if (error) return <Alert variant="danger">{error}</Alert>;

  // Today in NZ. This drives the "current week" highlight, so reading it off the
  // browser's clock put the marker on the wrong row for the first two hours of
  // each NZ day — and across a Sunday boundary, on the wrong WEEK.
  const todayIso = todayInput();
  // Data-bar scale: the year's best week (never below target, so a quiet
  // January doesn't inflate every bar).
  const maxLogged = report
    ? Math.max(
        report.weekly_target,
        ...report.months.flatMap((m) => m.weeks.filter((w) => !w.future).map((w) => w.logged))
      )
    : 1;
  // Months that haven't started yet are pure padding (Robert 2026-07-18):
  // show only months with at least one started week. The in-progress month
  // keeps its trailing (dimmed) future weeks; the Totals row still carries
  // the full prior-year sums, so no history is lost.
  const visibleMonths = report ? report.months.filter((m) => m.weeks.some((w) => !w.future)) : [];

  // House CSV pattern (worklist trio): quoted cells, filename from the title.
  // Two sections — the week grain, then the monthly rollup with prior years —
  // so the spreadsheet has both without repeating rollups on every row.
  function exportCsv() {
    if (!report) return;
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const row = (cells) => cells.map(esc).join(",");
    const lines = [row(["Month", "Week Start", "Week End", "Logs", "Target", "+/-", "Accepted"])];
    for (const m of visibleMonths) {
      for (const w of m.weeks) {
        if (w.future) continue;
        lines.push(row([m.month, w.start, w.end, w.logged, w.target, w.plus_minus, w.accepted]));
      }
    }
    lines.push(row(["Totals", "", "", report.totals.logged, report.totals.target, report.totals.plus_minus, report.totals.accepted]));
    lines.push("");
    lines.push(row([
      "Month", `${report.year} Logged`, `${report.year} Accepted`,
      ...report.prior_years.flatMap((y) => [`${y} Logged`, `${y} Accepted`]),
    ]));
    for (const m of visibleMonths) {
      lines.push(row([m.month, m.logged, m.accepted, ...m.prior.flatMap((p) => [p.logged, p.accepted])]));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `jobs-logged-accepted-${report.year}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <section className="page-panel la-print-wide">
      <div className="d-flex flex-wrap justify-content-between align-items-end column-gap-4 row-gap-2">
        <div className="page-heading">
          <h2>Jobs Logged / Accepted <RulesHelp topic="jobsLogged" /></h2>
        </div>
        <div className="d-flex align-items-center gap-2 d-print-none">
          <Button size="sm" variant="outline-success" onClick={exportCsv} disabled={!report}>CSV</Button>
          <Button size="sm" variant="outline-secondary" onClick={() => setYear((y) => y - 1)}>‹ {year - 1}</Button>
          <Form.Control
            size="sm" type="number" value={year} style={{ width: 90 }}
            onChange={(e) => setYear(Number(e.target.value) || year)}
          />
          <Button size="sm" variant="outline-secondary" disabled={year >= currentYear()}
            onClick={() => setYear((y) => y + 1)}>
            {year + 1} ›
          </Button>
        </div>
      </div>

      {!report ? (
        <p className="text-muted">Loading report…</p>
      ) : (
        <>
        <LoggedAcceptedHeadline report={{ ...report, months: visibleMonths }} />
        <div className="la-card">
        <Table responsive size="sm" className="align-middle la-report">
            <thead>
              <tr>
                <th colSpan={8} className="la-year">{report.year}</th>
                {report.prior_years.map((y) => (
                  <th key={y} colSpan={2} className="la-year la-group">{y}</th>
                ))}
              </tr>
              <tr>
                <th>Month</th>
                <th className="la-group">Week Dates</th>
                <th>Logs</th>
                <th>Target</th>
                <th>+/-</th>
                <th>Accepted Jobs</th>
                <th className="la-group">Monthly Logs</th>
                <th>Monthly Accepted</th>
                {report.prior_years.map((y) => (
                  <Fragment key={y}>
                    <th className="la-group">Logged</th>
                    <th>Accepted</th>
                  </Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleMonths.map((m) => (
                m.weeks.map((w, wi) => {
                  const isNow = !w.future && w.end >= todayIso;
                  return (
                    <tr key={w.start} className={wi === m.weeks.length - 1 ? "la-month-end" : ""}>
                      {wi === 0 ? (
                        <td rowSpan={m.weeks.length} className="la-month">{m.month}</td>
                      ) : null}
                      <td
                        className={`la-group ${w.future ? "la-dim" : ""} ${isNow ? "la-now" : ""}`}
                        data-hint={isNow ? "Current week (in progress)" : undefined}
                      >
                        {shortDate(w.start)} - {shortDate(w.end)}
                      </td>
                      <td
                        className="la-num la-bar"
                        style={w.future ? undefined : { "--la-fill": `${Math.round((w.logged / maxLogged) * 100)}%` }}
                      >
                        {w.future ? "" : w.logged}
                      </td>
                      <td className="la-num la-dim">{w.target}</td>
                      <td className="la-num">
                        {w.future ? (
                          <span className="la-dim">—</span>
                        ) : (
                          <span className={`la-pill ${w.plus_minus >= 0 ? "la-pill-good" : "la-pill-bad"}`}>
                            {w.plus_minus > 0 ? `+${w.plus_minus}` : w.plus_minus}
                          </span>
                        )}
                      </td>
                      <td className="la-num">{w.future ? "" : w.accepted}</td>
                      {wi === 0 ? (
                        <>
                          <td rowSpan={m.weeks.length} className="la-num la-rollup la-group">{m.logged}</td>
                          <td rowSpan={m.weeks.length} className="la-num la-rollup">{m.accepted}</td>
                          {m.prior.map((p) => (
                            <Fragment key={p.year}>
                              <td rowSpan={m.weeks.length} className="la-num la-prior la-group">{p.logged}</td>
                              <td rowSpan={m.weeks.length} className="la-num la-prior">{p.accepted}</td>
                            </Fragment>
                          ))}
                        </>
                      ) : null}
                    </tr>
                  );
                })
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2} className="text-end">Totals</td>
                <td className="la-num">{report.totals.logged}</td>
                <td className="la-num la-dim">{report.totals.target}</td>
                <td className="la-num">
                  <span className={`la-pill ${report.totals.plus_minus >= 0 ? "la-pill-good" : "la-pill-bad"}`}>
                    {report.totals.plus_minus > 0 ? `+${report.totals.plus_minus}` : report.totals.plus_minus}
                  </span>
                </td>
                <td className="la-num">{report.totals.accepted}</td>
                <td colSpan={2} className="la-group" />
                {report.totals.prior.map((p) => (
                  <Fragment key={p.year}>
                    <td className="la-num la-group">{p.logged}</td>
                    <td className="la-num">{p.accepted}</td>
                  </Fragment>
                ))}
              </tr>
            </tfoot>
        </Table>
        </div>
        </>
      )}
    </section>
  );
}

export default LoggedAcceptedReportPage;
